import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiMenu, FiX, FiArrowRight } = FiIcons;
import SafeIcon from '../../common/SafeIcon';

export default function NavBar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const BOOKING_URL = "https://link.nordiccrm.ca/widget/bookings/dorman-ernest-baltazar-personal-calendar-qg2bbc8wa";

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Speaking', path: '/speaking' },
    { name: 'Blueprint', path: '/blueprint' },
    { name: 'THRIVE', path: '/thrive' },
    { name: 'Resources', path: '/resources' },
    { name: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    if (mobileMenuOpen) {
      document.body.style.overflow = 'unset';
    }
  }, [location]);

  const toggleMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    document.body.style.overflow = !mobileMenuOpen ? 'hidden' : 'unset';
  };

  return (
    <>
      <header 
        className={`fixed top-0 w-full z-[100] transition-all duration-500 ${
          isScrolled || mobileMenuOpen 
            ? 'bg-kin-black/95 backdrop-blur-md py-4 shadow-xl' 
            : 'bg-transparent py-6 md:py-10'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
          
          <Link 
            to="/" 
            className="relative z-[110] flex items-center gap-3 transition-transform hover:scale-105 active:scale-95 duration-300"
          >
            <img 
              src="https://media-manager-c.questera.ai/greta-media/9c5adfa6bba615730f4558013336717062b18ab46d11ed47c31c88c932e61ffbf11018f89f2243f6aef891f7c9f9eda4/images/aW1hZ2UvanBlZw==/68d6537db3932682a335733b6ae3e309.jpeg" 
              alt="Kingdom Inspira Nova" 
              className="h-10 md:h-14 w-auto object-contain rounded-md"
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link 
                key={link.name} 
                to={link.path}
                className={`text-[10px] tracking-[0.25em] uppercase transition-all duration-300 hover:text-kin-gold ${
                  location.pathname === link.path 
                    ? 'text-kin-gold font-semibold' 
                    : 'text-kin-ivory/60'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <button 
            className="lg:hidden relative z-[110] text-kin-ivory p-2 focus:outline-none"
            onClick={toggleMenu}
            aria-label="Toggle Menu"
          >
            <div className="w-6 h-5 flex flex-col justify-between items-end">
              <motion.span 
                animate={mobileMenuOpen ? { rotate: 45, y: 8, width: "100%" } : { rotate: 0, y: 0, width: "100%" }}
                className="block h-[1.5px] bg-kin-ivory origin-right transition-all"
              />
              <motion.span 
                animate={mobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                className="block h-[1.5px] bg-kin-ivory w-2/3 transition-all"
              />
              <motion.span 
                animate={mobileMenuOpen ? { rotate: -45, y: -8, width: "100%" } : { rotate: 0, y: 0, width: "100%" }}
                className="block h-[1.5px] bg-kin-ivory origin-right transition-all"
              />
            </div>
          </button>
        </div>
      </header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[90] bg-kin-black pt-32 pb-12 px-8 flex flex-col lg:hidden overflow-y-auto"
          >
            <div className="flex flex-col space-y-6 mb-12">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                >
                  <Link 
                    to={link.path}
                    className={`text-3xl font-serif tracking-tight flex items-center justify-between group ${
                      location.pathname === link.path ? 'text-kin-gold' : 'text-kin-ivory'
                    }`}
                  >
                    <span>{link.name}</span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5 opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0 transition-all text-kin-gold" />
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-auto pt-8 border-t border-kin-ivory/10 space-y-8"
            >
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-[0.3em] text-kin-gold font-semibold">Ready to start?</p>
                <a 
                  href={BOOKING_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block text-kin-ivory/60 hover:text-kin-gold transition-colors text-sm"
                >
                  Book a Discovery Call
                </a>
              </div>
              <div className="flex space-x-6">
                <p className="text-[10px] uppercase tracking-[0.2em] text-kin-ivory/30">
                  © {new Date().getFullYear()} KIN Studio
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}